
import React, { useMemo } from "react";
import { X, ExternalLink, Link2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCampaignDays } from "../../../hooks/useCampaignDays";
import { useLanguage } from "@/contexts/LanguageContext";
import { addDays, parseISO } from "date-fns";
import type { Creative } from "../../../types";
import ScheduledDaySelect from "../ScheduledDaySelect";

interface CreativePreviewProps {
  creative: Creative;
  creativeIndex?: number;
  onRemove: () => void;
  onUpdateScheduledDays?: (days: number[]) => void;
  /** Days already taken by other creatives in the same location */
  takenDays?: number[];
  /**
   * When true, show the schedule selector (e.g., only after 2+ files exist).
   * Defaults to true.
   */
  showScheduling?: boolean;
  isUploading: boolean;
  aspectRatio?: "video" | "portrait" | "square";
}

const CreativePreview = ({
  creative,
  creativeIndex,
  onRemove,
  onUpdateScheduledDays,
  takenDays = [],
  showScheduling = true,
  isUploading,
  aspectRatio = "video"
}: CreativePreviewProps) => {
  const { totalDays } = useCampaignDays();
  const { language } = useLanguage();
  const isRtl = language === "ar";

  // Get campaign dates
  const { campaignDates } = useMemo(() => {
    const storedStartDate = localStorage.getItem("startDate");
    const start = storedStartDate ? parseISO(storedStartDate) : new Date();

    const dates: Date[] = [];
    for (let i = 0; i < totalDays; i++) {
      dates.push(addDays(start, i));
    }

    return { campaignDates: dates };
  }, [totalDays]);

  // Use empty array if no days selected - do NOT default to [1]
  const scheduledDays = creative.scheduledDays?.length ? creative.scheduledDays : [];
  const hasNoDaysSelected = scheduledDays.length === 0;

  const handleDaysChange = (days: number[]) => {
    onUpdateScheduledDays?.(days);
  };

  // Define aspect ratio classes
  const aspectRatioClass =
    aspectRatio === "portrait" ? "aspect-[9/16]" :
    aspectRatio === "square" ? "aspect-square" :
    "aspect-video";

  // Day selector component - show for multi-day campaigns when showScheduling is true
  const DaySelector = () => {
    // Only show if: multi-day campaign, callback provided, and showScheduling is enabled
    if (totalDays <= 1 || !onUpdateScheduledDays || !showScheduling) return null;

    return (
      <div className="mt-2">
        {hasNoDaysSelected && showScheduling && (
          <div className="flex items-center gap-1.5 mb-2 p-1.5 rounded-md bg-primary/10 dark:bg-primary/20 border border-primary/30 dark:border-primary/40">
            <AlertCircle className="h-3 w-3 text-primary shrink-0" />
            <span className="text-[10px] text-primary dark:text-primary">
              {isRtl ? "يرجى اختيار يوم واحد على الأقل" : "Please select at least one day"}
            </span>
          </div>
        )}
        <ScheduledDaySelect
          value={scheduledDays}
          campaignDates={campaignDates}
          onChange={handleDaysChange}
          takenDays={takenDays}
          size="sm"
        />
      </div>
    );
  };

  // Check if this is an external link (not a direct media file)
  if (creative.isExternalLink) {
    return (
      <div className="relative border rounded-lg overflow-hidden bg-muted">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Link2 className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{creative.name}</p>
              <a
                href={creative.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1 truncate"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="truncate">{creative.url}</span>
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </a>
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 flex-shrink-0"
              onClick={onRemove}
              disabled={isUploading}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <DaySelector />
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative border border-border rounded-lg overflow-hidden">
      <div className={`${aspectRatioClass} bg-muted/50 dark:bg-muted/20 flex items-center justify-center`}>
        {creative.type.startsWith('image/') ? (
          <img src={creative.url} alt={creative.name} className="max-w-full max-h-full object-contain" />
        ) : creative.type.startsWith('video/') ? (
          <video 
            src={creative.url} 
            controls 
            className="max-w-full max-h-full"
          />
        ) : (
          <div className="text-muted-foreground">Unsupported file type</div>
        )}
      </div>
      <Button
        variant="outline"
        size="icon"
        className="absolute top-2 right-2 h-6 w-6 bg-background/80 backdrop-blur-sm"
        onClick={onRemove}
        disabled={isUploading}
      >
        <X className="h-3 w-3" />
      </Button>
      <div className="p-2 bg-background border-t">
        <p className="text-xs truncate">{creative.name}</p>
        <p className="text-xs text-muted-foreground">
          {(creative.size / 1024 / 1024).toFixed(2)} MB
        </p>
        <DaySelector />
      </div>
    </div>
  );
};

export default CreativePreview;
